import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";

const TEL = "+36303915392";

const treatments = [
  {
    num: "01",
    name: "Svédmasszázs",
    tag: "Klasszikus alapok",
    description:
      "Klasszikus, alapos kezelés a teljes testen. Oldja az izomfeszültséget és támogatja a regenerációt.",
    image:
      "https://images.unsplash.com/photo-1600334129128-685c5582fd35?auto=format&fit=crop&w=940&q=80",
  },
  {
    num: "02",
    name: "Relaxáló masszázs",
    tag: "Mély nyugalom",
    description:
      "Lassú, finom mozdulatok – a nap feszültségének teljes feloldása. Ideális választás stresszes időszakok után.",
    image:
      "https://images.unsplash.com/photo-1544161515-4ab6ce6db874?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjd8MHwxfHNlYXJjaHwzfHxsdXh1cnklMjBzcGElMjBtYXNzYWdlJTIwdGhlcmFweXxlbnwwfHx8fDE3NzkzNjI4NDB8MA&ixlib=rb-4.1.0&q=85",
  },
  {
    num: "03",
    name: "Aromaterápiás masszázs",
    tag: "Illatok harmóniája",
    description:
      "Gondosan választott illóolajok és figyelmes érintés. Az érzékek harmonizálása, mély pihenés.",
    image:
      "https://images.unsplash.com/photo-1540555700478-4be289fbecef?auto=format&fit=crop&w=940&q=80",
  },
  {
    num: "04",
    name: "Frissítő masszázs",
    tag: "Energia & fókusz",
    description:
      "Élénkítő, közérzetjavító kezelés. Visszaadja a nap energiáját, segíti a vérkeringést és a fókuszt.",
    image:
      "https://images.unsplash.com/photo-1633526543913-d30e3c230d1f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjd8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBzcGElMjBtYXNzYWdlJTIwdGhlcmFweXxlbnwwfHx8fDE3NzkzNjI4NDB8MA&ixlib=rb-4.1.0&q=85",
  },
  {
    num: "05",
    name: "Hát-, nyak- és vállmasszázs",
    tag: "Célzott területek",
    description:
      "A modern életmód leggyakoribb feszültségpontjaira fókuszáló, gyors és érezhetően hatékony kezelés.",
    image:
      "https://images.unsplash.com/photo-1519823551278-64ac92734fb1?auto=format&fit=crop&w=940&q=80",
  },
  {
    num: "06",
    name: "Gyógymasszázs jellegű kezelés",
    tag: "Alapos mélymunka",
    description:
      "Célzott, mélyebb munka a tartós feszültségek és túlterhelt területek oldására – figyelmes, alapos jelenléttel.",
    image:
      "https://images.pexels.com/photos/9146381/pexels-photo-9146381.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  },
];

const PRICING = [
  { duration: "30 perc", price: "6 000 Ft" },
  { duration: "60 perc", price: "10 000 Ft" },
];

const Treatments = () => {
  return (
    <section
      id="treatments"
      data-testid="treatments-section"
      className="relative py-28 md:py-40 lg:py-48 bg-anthracite-900 overflow-hidden"
    >
      <div className="absolute top-20 left-0 font-serif text-[18rem] md:text-[26rem] text-white/[0.025] leading-none pointer-events-none select-none">
        02
      </div>

      <div className="max-w-[1500px] mx-auto px-6 md:px-10 lg:px-14 relative">
        {/* Section header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="grid grid-cols-12 gap-8 items-end mb-20 md:mb-28"
        >
          <div className="col-span-12 md:col-span-7">
            <div className="flex items-center gap-4 mb-6">
              <span className="text-[10px] uppercase tracking-[0.4em] text-gold-500/80">
                02 — Kezelések
              </span>
              <div className="w-16 h-px bg-gold-500/40" />
            </div>
            <h2
              data-testid="treatments-title"
              className="font-serif text-cream leading-[1.02] tracking-[-0.02em] font-light"
            >
              <span className="block text-4xl md:text-5xl lg:text-6xl">Nyugodt, figyelmes</span>
              <span className="block text-4xl md:text-5xl lg:text-6xl italic gold-gradient-text mt-2">
                kezelések.
              </span>
            </h2>
          </div>
          <div className="col-span-12 md:col-span-4 md:col-start-9">
            <p className="text-cream/55 font-light text-sm md:text-base leading-relaxed border-l border-gold-500/30 pl-6">
              Hat, tudatosan választott kezelés. Minden egyes alkalom a feltöltődést
              és a közérzet javítását szolgálja — sietség nélkül, valódi figyelemmel.
            </p>
          </div>
        </motion.div>

        {/* Editorial rows */}
        <div className="relative">
          <ul className="border-t border-gold-500/15">
            {treatments.map((t, i) => (
              <motion.li
                key={t.num}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.8, delay: (i % 3) * 0.08, ease: [0.16, 1, 0.3, 1] }}
                data-testid={`treatment-card-${i}`}
                className="group relative border-b border-gold-500/15 transition-colors duration-500 hover:bg-anthracite-950/40"
              >
                <a
                  href={`tel:${TEL}`}
                  data-testid={`treatment-cta-${i}`}
                  className="grid grid-cols-12 gap-3 md:gap-8 py-7 md:py-12 lg:py-14 items-center"
                >
                  {/* Number */}
                  <div className="col-span-2 md:col-span-1">
                    <span className="font-serif italic text-gold-500 text-lg sm:text-xl md:text-2xl">{t.num}</span>
                  </div>

                  {/* Name + tag */}
                  <div className="col-span-10 md:col-span-5 lg:col-span-5">
                    <div className="text-[9px] sm:text-[10px] uppercase tracking-[0.35em] sm:tracking-[0.4em] text-cream/40 mb-2 md:mb-3">
                      {t.tag}
                    </div>
                    <h3
                      data-testid={`treatment-name-${i}`}
                      className="font-serif text-xl sm:text-2xl md:text-3xl lg:text-4xl font-light text-cream group-hover:gold-gradient-text transition-all duration-500 leading-tight"
                    >
                      {t.name}
                    </h3>
                  </div>

                  {/* Mobile image */}
                  <div className="col-span-12 md:hidden mt-2">
                    <div className="aspect-[16/9] overflow-hidden">
                      <img src={t.image} alt={t.name} className="w-full h-full object-cover" loading="lazy" />
                    </div>
                  </div>

                  {/* Description */}
                  <div className="col-span-12 md:col-span-3 lg:col-span-3">
                    <p className="text-cream/60 font-light text-sm md:text-base leading-relaxed">
                      {t.description}
                    </p>
                  </div>

                  {/* Pricing options + arrow */}
                  <div className="col-span-12 md:col-span-3 lg:col-span-3 flex items-center justify-between md:justify-end gap-4 md:gap-6 pt-2 md:pt-0 border-t md:border-t-0 border-white/5">
                    <div className="flex-1 md:flex-none text-left md:text-right space-y-2">
                      {PRICING.map((p) => (
                        <div key={p.duration} className="flex items-baseline justify-start md:justify-end gap-3">
                          <span className="text-[9px] uppercase tracking-[0.3em] sm:tracking-[0.35em] text-cream/40 min-w-[55px] md:min-w-0">
                            {p.duration}
                          </span>
                          <span className="font-serif text-base sm:text-lg md:text-xl text-gold-500 italic md:min-w-[90px] md:text-right">
                            {p.price}
                          </span>
                        </div>
                      ))}
                    </div>
                    <div className="w-11 h-11 md:w-12 md:h-12 border border-gold-500/40 group-hover:bg-gold-500 group-hover:border-gold-500 flex items-center justify-center transition-all duration-500 flex-shrink-0">
                      <ArrowUpRight
                        className="w-4 h-4 text-gold-500 group-hover:text-anthracite-950 transition-all duration-500 group-hover:rotate-12"
                        strokeWidth={1.5}
                      />
                    </div>
                  </div>
                </a>
              </motion.li>
            ))}
          </ul>
        </div>

        {/* Footer note */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.3 }}
          className="mt-16 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6 text-xs uppercase tracking-[0.3em] text-cream/40"
        >
          <span>Minden kezelés 30 vagy 60 percben kérhető</span>
          <a
            href={`tel:${TEL}`}
            data-testid="treatments-footer-cta"
            className="text-gold-500 hover:text-gold-400 transition-colors"
          >
            Hívjon az időpontért →
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default Treatments;
